import * as PIXI from 'pixi.js';
import 'reflect-metadata';
import { Config } from '../../video-data/config';

import {
    NumberAttribute,
    ColorAttribute,
    AlphaAttribute,
    getAttributesToSerialize
} from '../attributes';
import type { BaseAssetSettings } from './BaseAsset';
import { BaseAsset } from './BaseAsset';

/**
 * A RectangleAsset Object will load the specified solid color.
 */
export class RectangleAsset extends BaseAsset {
    override _classType = Config.RECTANGLE_ASSET_TYPE;

    _graphics: PIXI.Graphics;

    @NumberAttribute({ min: 0 })
    public rectWidth = 10;
    @NumberAttribute({ min: 0 })
    public rectHeight = 10;
    @NumberAttribute({ min: 0 })
    public lineThickness = 0;
    @ColorAttribute()
    public lineColor = 16777215;
    @ColorAttribute()
    public color = 16777215;
    @AlphaAttribute()
    public fillAlpha = 1;
    @NumberAttribute({ min: 0, max: 1, serialize: false })
    public lineAlpha = 1;
    @NumberAttribute({ min: 0, max: 1, serialize: false })
    public lineAlignment = 0.5;

    /**
     * @param {Object} container - the container we want to house this asset under
     * @param {Object} settings - Configuration object
     */
    constructor(container: PIXI.Container, settings: BaseAssetSettings) {
        super(container, settings);

        // compose object
        this._graphics = new PIXI.Graphics();
        this._addInternalChild(this._graphics);
    }

    override get failedToLoad() {
        return false;
    }

    override get isLoaded() {
        return true;
    }

    /**
     * This asset doesn't have to load anything remotely so it should "always load"
     */
    override get shouldLoad() {
        return true;
    }

    protected override _build() {
        this.clearAnimation();
        this.applyAnimation();
        this.applyIntroAnimations();
        this.applyOutroAnimations();
    }

    override _draw() {
        this._graphics.clear();
        this._graphics.lineStyle(this.lineThickness, this.lineColor, this.lineAlpha, this.lineAlignment);
        this._graphics.beginFill(this.color, this.fillAlpha);
        this._graphics.drawRect(0, 0, this.rectWidth, this.rectHeight);
        this._graphics.endFill();

        this.applyTransformation();
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat(
            ['mask'],
            getAttributesToSerialize(this)
        );
    };
}
